import { queryNoTracking, throwIfFrameworkException } from '../../services/DataService';
import { Helper as messageService, MessageTypes } from '../../services/MessageService';
import { safeString } from './useAiChatAssistant';

const q_ChatStart = queryNoTracking('Workflow_QueryShaping_ChatStart', 0, true);
// One result endpoint serves every turn kind (query shaping, workflow JavaScript,
// custom validation JavaScript); the state's Kind says which response slot is set.
const q_ChatResult = queryNoTracking('Assistant_ChatResult', 0, true);

/**
 * The ONE definition of the assistant chat request envelope, shared by every
 * assistant client (the full AccentQueryShapingAssistantDlg and the chat-only
 * AccentAssistantChatPanel). Defaults describe a CHAT-ONLY turn — no artifacts, no
 * templates — and callers override just the fields their host actually uses, so a
 * server contract change only ever has one client-side home.
 */
export const buildAssistantChatRequest = (fields) => ({
    prompt: '',
    messages: [],
    queries: [],
    shapes: [],
    allowNewShapes: false,
    allowNewQueries: false,
    templateValues: {},
    queryAllowedTemplates: [],
    shapeAllowedTemplates: [],
    variablesJson: '{}',
    runtimeNotes: [],
    hostLabel: '',
    sessionOptions: {},
    promptIntent: '',
    // Screenshots pasted with this turn (base64 data URLs, downscaled client-side).
    // The server shows them to its models as images; earlier pastes travel on the
    // messages history (each user message's images).
    promptImages: [],
    ...fields,
});

// ── Async turn transport ────────────────────────────────────────────────────────
//
// A turn regularly runs past the Azure front-end's FIXED 230s request timeout; the
// gateway then kills the request and returns an HTML error page, which the old
// single-request transport choked on ("Unexpected token '<' ... is not valid JSON")
// even though the server finished the turn moments later. So no HTTP request may
// carry a whole turn anymore — for ANY assistant kind (query shaping, workflow
// JavaScript, custom validation JavaScript all ride this transport):
//
//   1. A *_ChatStart endpoint returns a turn id in milliseconds; the pipeline runs
//      on a detached server task, its state persisted in blob storage
//      (multi-instance safe).
//   2. The server pushes SignalR heartbeats while working and a final done/failed
//      signal (MessageTypes.AssistantTurn).
//   3. The client fetches the stored result via Assistant_ChatResult — immediately
//      on the done signal, or by polling (fast at first for short turns, then
//      relaxed) when SignalR is quiet, so the flow also works without a live socket.
//
// Failure is detected rather than assumed: a running turn is only declared dead
// when its server-side heartbeat timestamp goes stale (the instance running it
// died), when the turn state disappears, or when the server reports it failed.

/** Poll ChatResult quickly at first (short turns feel instant even without SignalR), then relax. */
const POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 12000, 15000];
/** The server refreshes the blob heartbeat every ~45s; 3 minutes without one means the runner died. */
const HEARTBEAT_STALE_MS = 3 * 60 * 1000;
/**
 * No total cap on a turn: the model may legitimately think for a long time (its calls
 * run in background mode on the server, so nothing is held open meanwhile), and a
 * healthy turn must never be failed for taking its time. Liveness is judged from the
 * heartbeat alone; the server's own generous cap (AssistantTurnTimeoutMinutes) ends a
 * turn that truly never finishes, and that arrives here as a 'failed' state.
 */
/** Grace for the very first polls: the state blob may trail the start response by a beat. */
const NOT_FOUND_GRACE_MS = 30 * 1000;
/** Consecutive ChatResult transport failures tolerated before giving up (network blips). */
const MAX_FETCH_ERRORS = 5;

const parseUtc = (v) => {
    const ms = Date.parse(safeString(v));
    return Number.isFinite(ms) ? ms : null;
};

const waitForAssistantTurn = (turnID) => new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const registrationKey = `assistantTurn_${turnID}`;
    let settled = false;
    let fetching = false;
    let pollIndex = 0;
    let pollTimer = null;
    let fetchErrors = 0;

    const settle = (fn, value) => {
        if (settled) return;
        settled = true;
        if (pollTimer) clearTimeout(pollTimer);
        messageService.unregister(registrationKey, MessageTypes.AssistantTurn);
        fn(value);
    };

    const schedulePoll = () => {
        if (settled) return;
        if (pollTimer) clearTimeout(pollTimer);
        const delay = POLL_DELAYS_MS[Math.min(pollIndex, POLL_DELAYS_MS.length - 1)];
        pollIndex++;
        pollTimer = setTimeout(() => fetchResult(), delay);
    };

    const fetchResult = async () => {
        if (settled || fetching) return;
        fetching = true;
        try {
            const state = throwIfFrameworkException(await q_ChatResult.getFirstOrDefault({ turnID }));
            fetchErrors = 0;

            const status = safeString(state?.Status ?? state?.status).toLowerCase();
            if (status === 'done') {
                // The populated slot depends on the turn kind: query-shaping turns fill
                // Response, JavaScript-assistant turns fill JavascriptResponse.
                const response = state?.Response ?? state?.response
                    ?? state?.JavascriptResponse ?? state?.javascriptResponse;
                if (response) {
                    settle(resolve, response);
                } else {
                    settle(reject, new Error('The assistant completed but returned no response.'));
                }
                return;
            }
            if (status === 'failed') {
                settle(reject, new Error(safeString(state?.Error ?? state?.error) || 'AI request failed.'));
                return;
            }
            if (status === 'notfound') {
                if (Date.now() - startedAt > NOT_FOUND_GRACE_MS) {
                    settle(reject, new Error('The assistant request was lost on the server. Please try again.'));
                    return;
                }
                schedulePoll();
                return;
            }

            // Still running: dead-runner detection via the server-side heartbeat (an app
            // recycle or instance loss mid-turn stops refreshing it).
            const heartbeatMs = parseUtc(state?.HeartbeatUtc ?? state?.heartbeatUtc);
            if (heartbeatMs != null && Date.now() - heartbeatMs > HEARTBEAT_STALE_MS) {
                settle(reject, new Error('The assistant stopped responding (the server may have restarted). Please try again.'));
                return;
            }
            schedulePoll();
        } catch (err) {
            fetchErrors++;
            if (fetchErrors >= MAX_FETCH_ERRORS) {
                settle(reject, err instanceof Error ? err : new Error(safeString(err?.message) || 'AI request failed.'));
                return;
            }
            schedulePoll();
        } finally {
            fetching = false;
        }
    };

    // SignalR accelerates the loop; polling above keeps it correct without it.
    messageService.register(registrationKey, MessageTypes.AssistantTurn, (message) => {
        if (settled) return;
        const msgTurnID = safeString(message?.TurnID ?? message?.turnID);
        if (msgTurnID !== turnID) return;
        const status = safeString(message?.Status ?? message?.status).toLowerCase();
        if (status === 'done' || status === 'failed') {
            if (pollTimer) clearTimeout(pollTimer);
            fetchResult();
        }
        // 'running' heartbeats need no action: liveness is judged from the blob's
        // HeartbeatUtc, so a dead SignalR channel can never fail a healthy turn.
    });

    schedulePoll();
});

/**
 * The kind-agnostic core: posts the request body to a *_ChatStart query, then
 * resolves with the completed response however long the turn takes (start +
 * push/poll + result fetch under the hood; see the transport notes above — no
 * single HTTP request ever spans the turn). Handled 500s on the start call return
 * the exception envelope instead of throwing; the guard throws its translated
 * message so callers report it as an error turn. Pipeline failures inside the
 * detached turn arrive as the stored error and are thrown the same way.
 */
const startAndAwaitAssistantTurn = async (startQuery, requestBody) => {
    const start = throwIfFrameworkException(await startQuery.getFirstOrDefault(requestBody));

    const ok = !!(start?.Ok ?? start?.ok);
    const turnID = safeString(start?.TurnID ?? start?.turnID);
    if (!ok || !turnID) {
        throw new Error(safeString(start?.Error ?? start?.error) || 'The assistant request could not be started.');
    }

    return await waitForAssistantTurn(turnID);
};

/**
 * Sends one query-shaping chat turn and resolves with the completed
 * ChatQueryShapingResponse. Fields are normalized through the shared envelope
 * (buildAssistantChatRequest).
 */
export const sendAssistantChatTurn = (fields) => startAndAwaitAssistantTurn(q_ChatStart, buildAssistantChatRequest(fields));

/**
 * Sends one JavaScript-assistant chat turn (workflow or custom validation — the
 * caller picks the *_ChatStart query, e.g. Workflow_Javascript_ChatStart) and
 * resolves with the completed ChatWorkflowJavascriptResponse. The request body is
 * sent as-is: it is a ChatWorkflowJavascriptRequest, not the query-shaping envelope.
 */
export const sendJavascriptAssistantChatTurn = (startQuery, request) => startAndAwaitAssistantTurn(startQuery, request ?? {});

/**
 * Normalizes the CHAT lane of a chat response (the server returns PascalCase, older
 * cached shapes camelCase). Artifact-heavy consumers (queries/shapes/checklist/review)
 * read those extra fields off the raw response themselves.
 */
export const parseAssistantChatResponse = (res) => ({
    message: safeString(res?.Message ?? res?.message),
    // The server's oracle round record: a hidden "tool" transcript message — never
    // rendered, but round-tripped so the next turn refines the previous brief.
    toolRecord: safeString(res?.ToolRecord ?? res?.toolRecord),
    // Optional second-audience evidence (the oracle's technical findings): the visible
    // message omits it; opt-in hosts render it behind a collapsed expander.
    technicalDetails: safeString(res?.TechnicalDetails ?? res?.technicalDetails),
    // Severity-typed server notices ('error' vs softer limitations/notes).
    notices: (res?.Notices ?? res?.notices ?? [])
        .map((n) => ({
            text: safeString(n?.Text ?? n?.text),
            severity: safeString(n?.Severity ?? n?.severity).toLowerCase(),
        }))
        .filter((n) => n.text),
    // Profile context contract violations (host wiring problems, not user errors).
    contextWarnings: (res?.ContextWarnings ?? res?.contextWarnings ?? []).map(safeString).filter(Boolean),
    // Pipeline usage counters for the turn (every AI model / data API call end-to-end).
    aiCallCount: Number(res?.AiCallCount ?? res?.aiCallCount ?? 0),
    dataApiCallCount: Number(res?.DataApiCallCount ?? res?.dataApiCallCount ?? 0),
});
